import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { 
  SellerLocation, 
  LocationType, 
  SellerStatus, 
  FulfillmentCapability, 
  AddSellerLocationRequest 
} from '../../../../api/shop/types/sellerTypes';
import { addSellerLocation, updateSellerLocation, getSellerLocationById } from '../../../../api/shop/sellers/sellerLocationService';
import { getAcademiesWithCenters, AcademyWithCenters, CoachingCenter } from '../../../../api/shop/academyService';
import ShimmerLoader from '../../../../Common/ShimmerLoader';
import '../../../../Common/FormStyles.css';
import './SellerFormModal.css';

interface LocationFormModalProps {
  show: boolean;
  onHide: () => void;
  sellerId: string;
  sellerFulfillmentCapabilities?: FulfillmentCapability[]; // Pass seller's fulfillment capabilities as prop
  location?: SellerLocation | null;
  onSuccess: () => void;
}

const LocationFormModal: React.FC<LocationFormModalProps> = ({
  show,
  onHide,
  sellerId,
  sellerFulfillmentCapabilities: propSellerFulfillmentCapabilities = [],
  location,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingAcademies, setLoadingAcademies] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [academies, setAcademies] = useState<AcademyWithCenters[]>([]);
  const [sellerFulfillmentCapabilities, setSellerFulfillmentCapabilities] = useState<FulfillmentCapability[]>(propSellerFulfillmentCapabilities);
  const [formData, setFormData] = useState<AddSellerLocationRequest>({
    seller: sellerId,
    locationCode: '',
    type: 'ACADEMY',
    name: '',
    address: {
      line1: '',
      city: '',
      state: '',
      pincode: '',
    },
    fulfillmentCapabilities: [],
    revenueCenterCode: null,
    status: 'ACTIVE',
    owner: undefined,
  });
  const [selectedAcademyId, setSelectedAcademyId] = useState<string | null>(null);
  const [selectedCoachingCenterId, setSelectedCoachingCenterId] = useState<string | null>(null);
  const [availableCoachingCenters, setAvailableCoachingCenters] = useState<CoachingCenter[]>([]);

  // Update seller fulfillment capabilities when prop changes
  useEffect(() => {
    setSellerFulfillmentCapabilities(propSellerFulfillmentCapabilities);
  }, [propSellerFulfillmentCapabilities]);

  // Fetch academies when form opens (needed for both add and edit)
  useEffect(() => {
    if (show && academies.length === 0) {
      fetchAcademies();
    }
  }, [show]);

  // Update available coaching centers when academy is selected
  useEffect(() => {
    if (selectedAcademyId && academies.length > 0) {
      const selectedAcademy = academies.find(academy => academy._id === selectedAcademyId);
      if (selectedAcademy) {
        setAvailableCoachingCenters(selectedAcademy.coachingCenters || []);
      } else {
        setAvailableCoachingCenters([]);
      }
    } else {
      setAvailableCoachingCenters([]);
    }
  }, [selectedAcademyId, academies]);

  // Fetch location data when editing
  useEffect(() => {
    if (show && location?._id) {
      // Always fetch fresh location data when editing
      // fetchLocationData will handle fetching academies if needed
      fetchLocationData();
    }
  }, [show, location?._id]);

  // Auto-populate fulfillment capabilities based on seller's capabilities
  useEffect(() => {
    if (sellerFulfillmentCapabilities.length > 0 && !location) {
      // Only auto-populate for new locations, not when editing
      const hasPickup = sellerFulfillmentCapabilities.includes('PICKUP');
      const hasDelivery = sellerFulfillmentCapabilities.includes('DELIVERY');

      if (hasPickup && !hasDelivery) {
        // Only PICKUP: auto-select PICKUP
        setFormData((prev) => ({
          ...prev,
          fulfillmentCapabilities: ['PICKUP'],
        }));
      } else if (!hasPickup && hasDelivery) {
        // Only DELIVERY: auto-select DELIVERY
        setFormData((prev) => ({
          ...prev,
          fulfillmentCapabilities: ['DELIVERY'],
        }));
      }
      // If both, user can select (no auto-selection)
    }
  }, [sellerFulfillmentCapabilities, location]);


  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        seller: sellerId,
        locationCode: '',
        type: 'ACADEMY',
        name: '',
        address: {
          line1: '',
          city: '',
          state: '',
          pincode: '',
        },
        fulfillmentCapabilities: [],
        revenueCenterCode: null,
        status: 'ACTIVE',
        owner: undefined,
      });
      setSelectedAcademyId(null);
      setSelectedCoachingCenterId(null);
      setError(null);
    }
  }, [show, sellerId]);

  const fetchLocationData = async () => {
    if (!location?._id) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const locationData = await getSellerLocationById(location._id);
      
      // Ensure academies are loaded before populating form (needed for academy/coaching center mapping)
      let academiesData = academies;
      if (academiesData.length === 0) {
        academiesData = await fetchAcademies();
      }
      
      // Populate form after academies are loaded, passing academies data directly
      populateFormFromLocation(locationData, academiesData);
    } catch (err: any) {
      setError('Failed to load location data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const populateFormFromLocation = (locationData: SellerLocation, academiesData: AcademyWithCenters[] = academies) => {
    // Map owner.ownerId back to academy and coaching center IDs
    let academyId: string | null = null;
    let coachingCenterId: string | null = null;

    if (locationData.owner?.ownerId && academiesData.length > 0) {
      // Find which academy contains this coaching center
      for (const academy of academiesData) {
        const center = academy.coachingCenters.find(c => c._id === locationData.owner?.ownerId);
        if (center) {
          academyId = academy._id;
          coachingCenterId = center._id;
          break;
        }
      }
    }

    setSelectedAcademyId(academyId);
    setSelectedCoachingCenterId(coachingCenterId);

    // Always preserve address data from fetched location, even for ACADEMY type
    // Backend requires address fields even for ACADEMY locations
    const addressData = locationData.address || {
      line1: '',
      city: '',
      state: '',
      pincode: '',
    };

    setFormData({
      seller: sellerId,
      locationCode: locationData.locationCode || '',
      type: locationData.type,
      name: locationData.name || '',
      address: {
        line1: addressData.line1 || '',
        city: addressData.city || '',
        state: addressData.state || '',
        pincode: addressData.pincode || '',
      },
      fulfillmentCapabilities: locationData.fulfillmentCapabilities || [],
      revenueCenterCode: locationData.revenueCenterCode || null,
      status: locationData.status,
      owner: locationData.owner,
    });
    setError(null);
  };

  const fetchAcademies = async (): Promise<AcademyWithCenters[]> => {
    try {
      setLoadingAcademies(true);
      const data = await getAcademiesWithCenters();
      setAcademies(data);
      return data;
    } catch (err: any) {
      setError('Failed to load academies. Please try again.');
      return [];
    } finally {
      setLoadingAcademies(false);
    }
  };

  // Smart address parsing helper function
  const parseAddressDetails = (address: string): { city: string; state: string; pincode: string } => {
    // Default values for common cases
    let city = 'Mumbai';
    let state = 'Maharashtra';
    let pincode = '400001';

    if (!address) return { city, state, pincode };

    const addressLower = address.toLowerCase();

    // Extract pincode (6-digit number)
    const pincodeMatch = address.match(/\b(\d{6})\b/);
    if (pincodeMatch) {
      pincode = pincodeMatch[1];
    }

    // Common city mappings
    const cityMappings = {
      'mumbai': 'Mumbai',
      'pune': 'Pune', 
      'thane': 'Thane',
      'navi mumbai': 'Navi Mumbai',
      'andheri': 'Mumbai',
      'bandra': 'Mumbai',
      'worli': 'Mumbai',
      'powai': 'Mumbai',
      'chembur': 'Mumbai',
      'malad': 'Mumbai',
      'borivali': 'Mumbai',
      'kandivali': 'Mumbai',
      'goregaon': 'Mumbai',
      'santacruz': 'Mumbai',
      'juhu': 'Mumbai',
      'colaba': 'Mumbai',
      'matunga': 'Mumbai',
      'dadar': 'Mumbai',
      'wadala': 'Mumbai',
      'ghatkopar': 'Mumbai',
      'bhandup': 'Mumbai',
      'mulund': 'Mumbai',
      'vikhroli': 'Mumbai',
      'dombivli': 'Thane',
      'kalyan': 'Thane'
    };

    // Check for city matches
    for (const [key, value] of Object.entries(cityMappings)) {
      if (addressLower.includes(key)) {
        city = value;
        break;
      }
    }

    // State mapping based on city
    if (['Mumbai', 'Pune', 'Thane', 'Navi Mumbai'].includes(city)) {
      state = 'Maharashtra';
    }

    // Pincode-based city detection for accuracy
    if (pincode.startsWith('40')) {
      state = 'Maharashtra';
      if (['400001', '400002', '400003', '400004', '400005', '400020', '400021', '400023', '400025', '400026', '400027', '400028', '400030', '400031', '400032', '400033', '400034', '400035', '400036', '400037', '400038', '400039', '400042', '400043', '400049', '400050', '400051', '400052', '400053', '400054', '400055', '400056', '400057', '400058', '400059', '400060', '400061', '400062', '400063', '400064', '400065', '400066', '400067', '400068', '400069', '400070', '400071', '400072', '400074', '400076', '400077', '400078', '400079', '400080', '400081', '400082', '400083', '400084', '400085', '400086', '400087', '400088', '400089', '400090', '400091', '400092', '400093', '400094', '400095', '400096', '400097', '400098', '400099'].includes(pincode)) {
        city = 'Mumbai';
      }
    } else if (pincode.startsWith('41')) {
      state = 'Maharashtra';
      city = 'Pune';
    } else if (pincode.startsWith('42')) {
      state = 'Maharashtra';
      city = 'Thane';
    }

    return { city, state, pincode };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (name: string, selectedOption: any) => {
    const value = selectedOption?.value || '';
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset academy and coaching center when type changes
    if (name === 'type') {
      setSelectedAcademyId(null);
      setSelectedCoachingCenterId(null);
      setFormData((prev) => ({
        ...prev,
        owner: undefined,
        name: '', // Reset name when switching types
        address: {
          line1: '',
          city: '',
          state: '',
          pincode: '',
        },
      }));

      // Reset fulfillment capabilities when type changes, then auto-populate based on seller
      const hasPickup = sellerFulfillmentCapabilities.includes('PICKUP');
      const hasDelivery = sellerFulfillmentCapabilities.includes('DELIVERY');

      if (hasPickup && !hasDelivery) {
        setFormData((prev) => ({
          ...prev,
          fulfillmentCapabilities: ['PICKUP'],
        }));
      } else if (!hasPickup && hasDelivery) {
        setFormData((prev) => ({
          ...prev,
          fulfillmentCapabilities: ['DELIVERY'],
        }));
      } else {
        // Both available, reset to empty (user will select)
        setFormData((prev) => ({
          ...prev,
          fulfillmentCapabilities: [],
        }));
      }
    }
  };

  const handleAcademyChange = (selectedOption: any) => {
    const academyId = selectedOption?.value || null;
    setSelectedAcademyId(academyId);
    setSelectedCoachingCenterId(null); // Reset coaching center when academy changes
    setFormData((prev) => ({
      ...prev,
      owner: undefined,
      name: '', // Reset name
      address: {
        line1: '',
        city: '',
        state: '',
        pincode: '',
      },
    }));
  };

  const handleCoachingCenterChange = (selectedOption: any) => {
    const centerId = selectedOption?.value || null;
    setSelectedCoachingCenterId(centerId);
    
    if (centerId && selectedAcademyId) {
      // Find the selected coaching center to get its address and location
      const selectedCenter = availableCoachingCenters.find(c => c._id === centerId);
      
      // Parse address details for auto-population
      const addressDetails = parseAddressDetails(selectedCenter?.address || '');
      
      setFormData((prev) => ({
        ...prev,
        owner: {
          ownerType: 'ACADEMY_COACHING_CENTER',
          ownerId: centerId,
        },
        name: selectedCenter?.location || '', // Use location as name
        address: {
          line1: selectedCenter?.address || '', // Auto-populate line1 from coaching center
          city: addressDetails.city, // Auto-populate city from parsing
          state: addressDetails.state, // Auto-populate state from parsing
          pincode: addressDetails.pincode, // Auto-populate pincode from parsing
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        owner: undefined,
        name: '',
        address: {
          line1: '',
          city: '',
          state: '',
          pincode: '',
        },
      }));
    }
  };

  const handleFulfillmentChange = (capability: FulfillmentCapability, checked: boolean) => {
    setFormData((prev) => {
      const current = prev.fulfillmentCapabilities || [];
      
      // Prevent unchecking if it's the only selected capability
      if (!checked && current.length === 1 && current.includes(capability)) {
        return prev; // Don't allow unchecking the last one
      }

      if (checked) {
        return {
          ...prev,
          fulfillmentCapabilities: [...current, capability],
        };
      } else {
        return {
          ...prev,
          fulfillmentCapabilities: current.filter((c) => c !== capability),
        };
      }
    });
  };

  // Helper function to check if a fulfillment capability should be disabled
  const isFulfillmentDisabled = (capability: FulfillmentCapability): boolean => {
    const hasPickup = sellerFulfillmentCapabilities.includes('PICKUP');
    const hasDelivery = sellerFulfillmentCapabilities.includes('DELIVERY');

    // If seller doesn't support this capability, disable it
    if (capability === 'PICKUP' && !hasPickup) return true;
    if (capability === 'DELIVERY' && !hasDelivery) return true;

    // If seller has only one capability, disable the other
    if (hasPickup && !hasDelivery && capability === 'DELIVERY') return true;
    if (!hasPickup && hasDelivery && capability === 'PICKUP') return true;

    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.fulfillmentCapabilities.length === 0) {
      setError('At least one fulfillment capability is required.');
      return;
    }

    if (formData.type === 'ACADEMY' && !selectedCoachingCenterId) {
      setError('Please select an academy and coaching center.');
      return;
    }

    // For ACADEMY type, address fields are auto-populated, so no manual validation needed

    if (formData.type === 'WAREHOUSE') {
      if (!formData.name || !formData.name.trim()) {
        setError('Name is required for Warehouse type.');
        return;
      }
      if (!formData.address.line1 || !formData.address.line1.trim()) {
        setError('Address Line 1 is required for Warehouse type.');
        return;
      }
      if (!formData.address.city || !formData.address.city.trim()) {
        setError('City is required for Warehouse type.');
        return;
      }
      if (!formData.address.state || !formData.address.state.trim()) {
        setError('State is required for Warehouse type.');
        return;
      }
      if (!formData.address.pincode || !formData.address.pincode.trim()) {
        setError('Pincode is required for Warehouse type.');
        return;
      }
    }

    setLoading(true);

    try {
      const isEditMode = !!location?._id;

      if (isEditMode) {
        // UPDATE: Use same structure as ADD (owner object)
        const payload: any = {
          seller: sellerId,
          locationCode: formData.locationCode,
          type: formData.type,
          fulfillmentCapabilities: formData.fulfillmentCapabilities,
          revenueCenterCode: formData.revenueCenterCode || null,
          status: formData.status,
        };

        // Add owner for ACADEMY type if a coaching center is selected
        if (formData.type === 'ACADEMY' && selectedCoachingCenterId) {
          payload.owner = {
            ownerType: 'ACADEMY_COACHING_CENTER',
            ownerId: selectedCoachingCenterId,
          };
        }

        // Add name for both types
        if (formData.name && formData.name.trim()) {
          payload.name = formData.name.trim();
        }

        // Address is always required - all fields must be populated
        payload.address = {
          line1: formData.address?.line1 || '',
          city: formData.address?.city || '',
          state: formData.address?.state || '',
          pincode: formData.address?.pincode || '',
        };

        await updateSellerLocation(location._id, payload);
      } else {
        // ADD: Use owner object
        const payload: any = {
          seller: sellerId,
          locationCode: formData.locationCode,
          type: formData.type,
          fulfillmentCapabilities: formData.fulfillmentCapabilities,
          revenueCenterCode: formData.revenueCenterCode || null,
          status: formData.status,
        };

        // Add owner only for ACADEMY type if a coaching center is selected
        if (formData.type === 'ACADEMY' && selectedCoachingCenterId) {
          payload.owner = {
            ownerType: 'ACADEMY_COACHING_CENTER',
            ownerId: selectedCoachingCenterId,
          };
        }

        // Add name for both types (ACADEMY gets coaching center location, WAREHOUSE gets manual entry)
        if (formData.name && formData.name.trim()) {
          payload.name = formData.name.trim();
        }

        // Address handling
        if (formData.type === 'WAREHOUSE') {
          payload.address = formData.address;
        } else {
          // For ACADEMY, use address from coaching center (stored in formData.address)
          payload.address = {
            line1: formData.address?.line1 || '',
            city: formData.address?.city || '',
            state: formData.address?.state || '',
            pincode: formData.address?.pincode || '',
          };
        }

        await addSellerLocation(payload);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      // Try to extract error message from various possible structures
      let errorMessage = 'Failed to save location. Please try again.';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Check for error.message
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        // Check for error.error.message
        else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        }
        // Check for error.error (if it's a string)
        else if (errorData.error && typeof errorData.error === 'string') {
          errorMessage = errorData.error;
        }
        // Check for error field directly
        else if (errorData.error && typeof errorData.error === 'object') {
          errorMessage = errorData.error.message || errorData.error.code || 'Invalid request';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setFormData({
      seller: sellerId,
      locationCode: '',
      type: 'ACADEMY',
      name: '',
      address: {
        line1: '',
        city: '',
        state: '',
        pincode: '',
      },
      fulfillmentCapabilities: [],
      revenueCenterCode: null,
      status: 'ACTIVE',
      owner: undefined,
    });
    setSelectedAcademyId(null);
    setSelectedCoachingCenterId(null);
    onHide();
  };

  const academyOptions = academies.map(academy => ({
    value: academy._id,
    label: academy.name,
  }));

  const coachingCenterOptions = availableCoachingCenters.map(center => ({
    value: center._id,
    label: center.location,
  }));

  return (
    <Offcanvas 
      show={show} 
      onHide={handleClose} 
      placement="end" 
      className="seller-form-sidebar"
      backdrop="static"
      keyboard={false}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{location ? 'Edit Seller Location' : 'Add New Seller Location'}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loading && !error ? (
          <ShimmerLoader type="form" />
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Location Code and Type - Two Fields in One Row */}
            <div className="row mb-3">
              <div className="col-6">
                <Form.Group>
                  <Form.Label>
                    Location Code<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="locationCode"
                    value={formData.locationCode}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., SFL-BLR-02"
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group>
                  <Form.Label>
                    Type<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix="react-select"
                    value={{
                      value: formData.type,
                      label: formData.type === 'ACADEMY' ? 'Academy' : 'Warehouse'
                    }}
                    onChange={(selectedOption) => handleSelectChange('type', selectedOption)}
                    options={[
                      { value: 'ACADEMY', label: 'Academy' },
                      { value: 'WAREHOUSE', label: 'Warehouse' }
                    ]}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    maxMenuHeight={200}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                  />
                </Form.Group>
              </div>
            </div>

            {/* Academy and Coaching Center - Two Fields in One Row (Conditional, shown only if Type is ACADEMY) */}
            {formData.type === 'ACADEMY' && (
              <div className="row mb-3">
                <div className="col-6">
                  <Form.Group>
                    <Form.Label>
                      Academy<span className="required-asterisk">*</span>
                    </Form.Label>
                    {loadingAcademies ? (
                      <div style={{ height: '38px', display: 'flex', alignItems: 'center' }}>
                        <ShimmerLoader type="form" />
                      </div>
                    ) : (
                      <Select
                        classNamePrefix="react-select"
                        value={selectedAcademyId ? academyOptions.find(opt => opt.value === selectedAcademyId) : null}
                        onChange={handleAcademyChange}
                        options={academyOptions}
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        maxMenuHeight={200}
                        placeholder="Select Academy"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    )}
                  </Form.Group>
                </div>
                <div className="col-6">
                  <Form.Group>
                    <Form.Label>
                      Coaching Center<span className="required-asterisk">*</span>
                    </Form.Label>
                    {selectedAcademyId ? (
                      <Select
                        classNamePrefix="react-select"
                        value={selectedCoachingCenterId ? coachingCenterOptions.find(opt => opt.value === selectedCoachingCenterId) : null}
                        onChange={handleCoachingCenterChange}
                        options={coachingCenterOptions}
                        isSearchable={true}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        maxMenuHeight={200}
                        placeholder="Select Coaching Center"
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    ) : (
                      <Select
                        classNamePrefix="react-select"
                        value={null}
                        options={[]}
                        isDisabled={true}
                        placeholder="Select Academy first"
                      />
                    )}
                  </Form.Group>
                </div>
              </div>
            )}

            {/* Name - Shown for both types but different behaviors */}
            {formData.type === 'ACADEMY' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Location Name
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Location name will be auto-populated from coaching center"
                  readOnly={!!selectedCoachingCenterId} // Read-only when coaching center is selected
                />
                <Form.Text className="text-muted">
                  Location name is automatically set from the selected coaching center
                </Form.Text>
              </Form.Group>
            )}

            {/* Name - Only shown for WAREHOUSE */}
            {formData.type === 'WAREHOUSE' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Name<span className="required-asterisk">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter warehouse name"
                />
              </Form.Group>
            )}

            {/* Address Fields for ACADEMY - Only Address Line 1 visible, City/State/Pincode auto-populated in background */}
            {formData.type === 'ACADEMY' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Address Line 1
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="address.line1"
                  value={formData.address.line1}
                  placeholder="Address will be auto-populated from coaching center"
                  readOnly
                />
                <Form.Text className="text-muted">
                  Address details are automatically set from the selected coaching center
                </Form.Text>
              </Form.Group>
            )}

            {/* Address Fields - Only shown for WAREHOUSE */}
            {formData.type === 'WAREHOUSE' && (
              <>
                {/* Address Line 1 - Full Width */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    Address Line 1<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="address.line1"
                    value={formData.address.line1}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter address line 1"
                  />
                </Form.Group>

                {/* City and State - Two Fields in One Row */}
                <div className="row mb-3">
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label>
                        City<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label>
                        State<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter state"
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* Pincode and Status - Two Fields in One Row */}
                <div className="row mb-3">
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label>
                        Pincode<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address.pincode"
                        value={formData.address.pincode}
                        onChange={handleInputChange}
                        required
                        placeholder="Enter pincode"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label>
                        Status<span className="required-asterisk">*</span>
                      </Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        value={{
                          value: formData.status,
                          label: formData.status === 'ACTIVE' ? 'Active' : 'Inactive'
                        }}
                        onChange={(selectedOption) => handleSelectChange('status', selectedOption)}
                        options={[
                          { value: 'ACTIVE', label: 'Active' },
                          { value: 'INACTIVE', label: 'Inactive' }
                        ]}
                        isSearchable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        maxMenuHeight={200}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Form.Group>
                  </div>
                </div>
              </>
            )}

            {/* Status - Only shown for ACADEMY (since address fields are hidden) */}
            {formData.type === 'ACADEMY' && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Status<span className="required-asterisk">*</span>
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={{
                    value: formData.status,
                    label: formData.status === 'ACTIVE' ? 'Active' : 'Inactive'
                  }}
                  onChange={(selectedOption) => handleSelectChange('status', selectedOption)}
                  options={[
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'INACTIVE', label: 'Inactive' }
                  ]}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  maxMenuHeight={200}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
              </Form.Group>
            )}

            {/* Fulfillment Capabilities */}
            <Form.Group className="mb-3">
              <Form.Label>
                Fulfillment Capabilities<span className="required-asterisk">*</span>
              </Form.Label>
              <div className="fulfillment-checkboxes">
                <Form.Check
                  type="checkbox"
                  id="location-fulfillment-pickup"
                  label="Pickup"
                  checked={formData.fulfillmentCapabilities.includes('PICKUP')}
                  onChange={(e) => handleFulfillmentChange('PICKUP', e.target.checked)}
                  disabled={isFulfillmentDisabled('PICKUP')}
                />
                <Form.Check
                  type="checkbox"
                  id="location-fulfillment-delivery"
                  label="Delivery"
                  checked={formData.fulfillmentCapabilities.includes('DELIVERY')}
                  onChange={(e) => handleFulfillmentChange('DELIVERY', e.target.checked)}
                  disabled={isFulfillmentDisabled('DELIVERY')}
                />
              </div>
            </Form.Group>

            {/* Revenue Center Code - Optional */}
            <Form.Group className="mb-3">
              <Form.Label>Revenue Center Code</Form.Label>
              <Form.Control
                type="text"
                name="revenueCenterCode"
                value={formData.revenueCenterCode || ''}
                onChange={handleInputChange}
                placeholder="Optional"
              />
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end mt-5">
              <Button variant="light" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading || formData.fulfillmentCapabilities.length === 0}
              >
                {loading ? 'Saving...' : location ? 'Update Location' : 'Create Location'}
              </Button>
            </div>
          </Form>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default LocationFormModal;
